import { OverlayModule } from '@angular/cdk/overlay';
import { CommonModule } from '@angular/common';
import {
  AfterViewInit,
  Component,
  ElementRef,
  signal,
  ViewChild,
} from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import {
  MatAutocomplete,
  MatAutocompleteModule,
} from '@angular/material/autocomplete';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { ICellEditorAngularComp } from 'ag-grid-angular';
import { ICellEditorParams } from 'ag-grid-community';
import { merge, Observable, of, Subject } from 'rxjs';
import {
  catchError,
  debounceTime,
  exhaustMap,
  filter,
  finalize,
  map,
  scan,
  startWith,
  switchMap,
  tap,
} from 'rxjs/operators';

export interface AutocompleteOptionDisplay {
  primary: string;
  secondary?: string;
}

export interface PagedResponse<T> {
  data: T[];
  pagination: {
    offset: number;
    recordCount: number;
    totalElements: number;
  };
}

type InputEditorParams = ICellEditorParams & {
  placeholder: string;
  maxLength: number;
  getAutocompleteOptions: (
    searchTerm: string,
    offset?: number,
    recordCount?: number
  ) => Observable<any[] | PagedResponse<any>>;
  minSearchTermLength: number;
  enableAutocomplete: boolean;
  displayWith: (option: any) => string;
  optionDisplayWith?: (option: any) => AutocompleteOptionDisplay;
  autocompletePanelClass?: string;
  showNoResultsOption?: boolean;
  noResultsText?: string;
  autocompleteVariant?: 'default' | 'multi';
  isPaging?: boolean;
  keepInvalidAutocompleteTextWhen?: (value: string) => boolean;
  minSearchMessage?: string;
  showMinSearchValidation?: boolean;
  loaderText?: string;
};

@Component({
  selector: 'lib-input-editor-with-options',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatIconModule,
    MatAutocompleteModule,
    ReactiveFormsModule,
    MatProgressSpinnerModule,
    OverlayModule,
  ],
  templateUrl: './input-editor.component.html',
  styleUrls: ['./input-editor.component.scss'],
})
export class InputEditorComponent
  implements ICellEditorAngularComp, AfterViewInit
{
  private params!: InputEditorParams;
  private initialValue: any;

  public placeholder!: string;
  public maxLength = 100;
  public showTooltip = signal(false);
  cellWidth = 0;
  cellHeight = 0;
  public panelWidth: string | number = 'auto';
  public searchControl = new FormControl();
  public filteredOptions$!: Observable<any[]>;
  private getAutocompleteOptions!: (
    searchTerm: string,
    offset?: number,
    recordCount?: number
  ) => Observable<any[] | PagedResponse<any>>;
  private minSearchTermLength = 3;
  public enableAutocomplete = false;
  public displayWith!: (option: any) => string;

  public optionDisplayWith!: (option: any) => AutocompleteOptionDisplay;
  public autocompletePanelClass = 'custom-autocomplete';
  public showNoResultsOption = false;
  public noResultsText!: string;
  public noResults = signal(false);
  public showMinSearchError = signal(false);
  public minSearchMessage = '';
  public showMinSearchValidation = false;
  public autocompleteVariant: 'default' | 'multi' = 'default';
  public loader = signal(false);
  private currentOptions: any[] = [];
  public loaderText?: string;

  public isPaging = false;
  private offset = 0;
  private readonly recordCount = 100;
  private totalElements = 0;
  private loading = false;

  private readonly nextPage$ = new Subject<void>();
  @ViewChild('auto') matAutocomplete!: MatAutocomplete;
  @ViewChild('inputWithOptions')
  private readonly input!: ElementRef<HTMLInputElement>;
  public readonly showClearIcon = toSignal(
    this.searchControl.valueChanges.pipe(
      map((val) => !!this.getDisplayText(val)?.trim())
    ),
    { initialValue: !!this.getDisplayText(this.searchControl.value)?.trim() }
  );
  ngAfterViewInit() {
    setTimeout(() => this.input.nativeElement.focus());

    this.matAutocomplete.opened.subscribe(() => {
      if (this.isPaging) {
        const panel = this.matAutocomplete.panel;
        if (panel) {
          panel.nativeElement.addEventListener(
            'scroll',
            this.onScroll.bind(this)
          );
        }
      }
    });
  }

  onScroll(event: Event): void {
    const target = event.target as HTMLElement;
    if (target.scrollTop + target.clientHeight >= target.scrollHeight - 1) {
      if (!this.loading && this.currentOptions.length < this.totalElements) {
        this.nextPage$.next();
      }
    }
  }

  agInit(params: InputEditorParams): void {
    this.params = params;
    this.initialValue = params.value;
    const value = this.params.value;
    this.placeholder = this.params.placeholder || '';
    this.maxLength = this.params.maxLength || 255;
    this.minSearchTermLength = this.params.minSearchTermLength || 3;
    this.enableAutocomplete = this.params.enableAutocomplete || false;
    this.loaderText = this.params.loaderText;
    this.getAutocompleteOptions =
      this.params.getAutocompleteOptions || (() => of([]));
    this.displayWith = this.params.displayWith || ((option: any) => option);

    this.optionDisplayWith =
      this.params.optionDisplayWith ||
      ((option: any) => ({
        primary: this.displayWith(option),
      }));

    this.autocompletePanelClass =
      this.params.autocompletePanelClass || 'custom-autocomplete';

    this.showNoResultsOption = this.params.showNoResultsOption || false;
    this.noResultsText = this.params.noResultsText || '';
    this.minSearchMessage = this.params.minSearchMessage || '';
    this.showMinSearchValidation = this.params.showMinSearchValidation || false;
    this.autocompleteVariant = this.params.autocompleteVariant || 'default';

    const cell = params.eGridCell;
    this.cellWidth = cell.clientWidth + 4;
    this.cellHeight = cell.clientHeight + 4;
    this.isPaging = this.params.isPaging || false;
    this.panelWidth = this.cellWidth;
    this.noResults.set(false);
    this.loader.set(false);
    this.searchControl.setValue(value);

    if (this.isPaging) {
      this.filteredOptions$ = this.searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        switchMap((val) => {
          const searchTerm =
            typeof val === 'object' && val ? this.displayWith(val) : val;

          if (typeof searchTerm !== 'string') {
            return of([]);
          }
          if (
            typeof searchTerm === 'string' &&
            this.handleMinSearchValidation(searchTerm)
          ) {
            return of([]);
          }
          this.offset = 0;
          this.currentOptions = [];
          this.totalElements = 0;
          this.noResults.set(false);
          this.loading = false;

          if (searchTerm.length < this.minSearchTermLength) {
            this.loader.set(false);
            return of([]);
          }

          const initialLoad$ = this.fetchPagedOptions(
            searchTerm,
            this.offset
          ).pipe(
            catchError(() => {
              this.loader.set(false);
              return of({
                data: [],
                pagination: { totalElements: 0, offset: 0, recordCount: 0 },
                page: 0,
              });
            })
          );

          const subsequentPages$ = this.nextPage$.pipe(
            filter(
              () =>
                !this.loader() &&
                this.currentOptions?.length < this.totalElements
            ),
            tap(() => (this.offset += this.recordCount)),
            exhaustMap(() =>
              this.fetchPagedOptions(searchTerm, this.offset).pipe(
                catchError(() => {
                  this.loader.set(false);
                  return of({
                    data: [],
                    pagination: {
                      totalElements: this.totalElements,
                      offset: this.offset,
                      recordCount: 0,
                    },
                    page: this.offset,
                  });
                })
              )
            )
          );

          return merge(initialLoad$, subsequentPages$).pipe(
            scan((acc, response) => {
              this.totalElements = response?.pagination?.totalElements ?? 0;
              const data = response?.data ?? [];
              if (response?.page === 0) {
                return data;
              }
              return [...acc, ...data];
            }, [] as any[]),
            tap((options) => {
              this.currentOptions = options;
              if (
                this.currentOptions.length >= this.totalElements ||
                this.totalElements === 0
              ) {
                this.loader.set(false);
                const hasNoResults =
                  this.currentOptions.length === 0 && this.totalElements === 0;
                this.noResults.set(hasNoResults);
              }
            })
          );
        })
      );
    } else {
      this.filteredOptions$ = this.searchControl.valueChanges.pipe(
        startWith(''),
        debounceTime(300),
        switchMap((val) => {
          const searchTerm =
            typeof val === 'object' && val ? this.displayWith(val) : val;

          this.noResults.set(false);
          //show min char message
          if (
            typeof searchTerm === 'string' &&
            this.handleMinSearchValidation(searchTerm)
          ) {
            return of([]);
          }
          const shouldSkipLookup =
            typeof searchTerm === 'string' &&
            this.params.keepInvalidAutocompleteTextWhen?.(searchTerm);

          if (shouldSkipLookup) {
            this.loader.set(false);
            this.noResults.set(false);
            this.currentOptions = [];
            return of([]);
          }

          if (
            this.enableAutocomplete &&
            typeof searchTerm === 'string' &&
            searchTerm.length >= this.minSearchTermLength
          ) {
            this.loader.set(true);

            return (
              this.getAutocompleteOptions(searchTerm) as Observable<any[]>
            ).pipe(
              tap((options) => {
                this.currentOptions = options;
                const hasNoResults =
                  this.showNoResultsOption &&
                  Array.isArray(options) &&
                  options.length === 0;

                this.noResults.set(hasNoResults);
              }),
              catchError(() => {
                this.noResults.set(this.showNoResultsOption);
                this.currentOptions = [];
                return of([]);
              }),
              finalize(() => {
                this.loader.set(false);
              }),
              startWith([])
            );
          }

          this.loader.set(false);
          this.noResults.set(false);
          this.currentOptions = [];

          return of([]);
        })
      );
    }
  }

  onOptionSelected(option: any) {
    this.searchControl.setValue(option);
  }

  private handleMinSearchValidation(searchTerm: string): boolean {
    if (
      this.showMinSearchValidation &&
      searchTerm.length > 0 &&
      searchTerm.length < this.minSearchTermLength
    ) {
      this.minSearchMessage = this.params.minSearchMessage || '';
      // Reset previous states
      this.noResults.set(false);
      this.showMinSearchError.set(true);
      this.loader.set(false);
      this.currentOptions = [];
      return true;
    }

    this.showMinSearchError.set(false);
    return false;
  }

  public onBlur(): void {
    if (!this.enableAutocomplete) {
      return;
    }

    if (!this.searchControl.dirty) {
      return;
    }

    const value = this.searchControl.value;

    if (!value || typeof value === 'object') {
      return;
    }

    const matchedOption = this.findMatchingOption(value);

    if (matchedOption) {
      this.searchControl.setValue(matchedOption, { emitEvent: false });
      return;
    }

    const typedText = this.getDisplayText(value);

    const shouldKeepInvalidText =
      typeof value === 'string' &&
      this.params.keepInvalidAutocompleteTextWhen?.(typedText);

    if (shouldKeepInvalidText) {
      return;
    }

    this.searchControl.setValue('');
  }

  private fetchPagedOptions(
    searchTerm: string,
    offset: number
  ): Observable<PagedResponse<any> & { page: number }> {
    if (
      !this.enableAutocomplete ||
      searchTerm.length < this.minSearchTermLength
    ) {
      return of({
        data: [],
        pagination: {
          offset: offset,
          recordCount: this.recordCount,
          totalElements: 0,
        },
        page: offset,
      });
    }

    this.loading = true;
    this.loader.set(true);

    return (
      this.getAutocompleteOptions(
        searchTerm,
        offset,
        this.recordCount
      ) as Observable<PagedResponse<any>>
    ).pipe(
      map((response) => ({ ...response, page: offset })),
      tap(() => {
        this.loading = false;
        this.loader.set(false);
      }),
      catchError(() => {
        this.loading = false;
        this.loader.set(false);
        return of({
          data: [],
          pagination: {
            offset: offset,
            recordCount: this.recordCount,
            totalElements: 0,
          },
          page: offset,
        });
      })
    );
  }

  getValue(): any {
    const value = this.searchControl.value;

    if (this.enableAutocomplete && typeof value === 'string') {
      const matchedOption = this.findMatchingOption(value);

      if (matchedOption) {
        return matchedOption;
      }

      const typedText = this.getDisplayText(value);

      const shouldKeepInvalidText =
        this.params.keepInvalidAutocompleteTextWhen?.(typedText);

      if (shouldKeepInvalidText || value === this.initialValue) {
        return value;
      }

      return '';
    }

    return value;
  }

  clearInput(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.showTooltip.set(false);
    this.searchControl.setValue('');

    if (this.params.data) {
      this.params.data.validationErrors = {};
    }

    this.params.api.refreshCells({
      rowNodes: [this.params.node],
      force: true,
    });

    setTimeout(() => {
      this.input?.nativeElement.focus();
    });
  }
  private getDisplayText(value: any): string {
    if (!value) {
      return '';
    }

    return typeof value === 'string' ? value : this.displayWith(value);
  }

  private findMatchingOption(value: any): any {
    const inputText = this.getDisplayText(value).trim();

    if (!inputText) {
      return undefined;
    }

    return this.currentOptions?.find((option) => {
      const optionText = this.displayWith(option)?.trim();

      if (optionText !== inputText) {
        return false;
      }

      if (typeof option === typeof value) {
        return true;
      }

      return (
        typeof value === 'string' &&
        option !== null &&
        typeof option === 'object'
      );
    });
  }
}
